/** @typedef {[number, number]} Point */
/** @typedef {{ x: number; y: number }} Vec2 */
/** @typedef {import('./floorPlanDesignerPivotWallPolygons.js').DesignerGraph} DesignerGraph */
/** @typedef {import('./floorPlanDesignerState.js').DesignerWall} DesignerWall */

import {
  DEFAULT_WALL_THICKNESS_M,
  MIN_WALL_LENGTH_METERS,
  getPointById,
} from './floorPlanDesignerState.js';
import {
  toDesignerGraph,
  validatePivotWallPolygon,
} from './floorPlanDesignerPivotWallPolygons.js';

export const NODE_EPS_PX = 0.5;
export const MAX_JOIN_DISTANCE_FACTOR = 8;

const EPS = 1e-9;
const PARALLEL_EPS = 1e-8;

/**
 * @typedef {{
 *   wallId: string;
 *   end: 'start' | 'end';
 *   nodeKey: string;
 *   nodePoint: Point;
 *   otherPoint: Point;
 *   directionFromNode: Vec2;
 *   angle: number;
 *   leftNormal: Vec2;
 *   rightNormal: Vec2;
 *   leftPoint: Point;
 *   rightPoint: Point;
 *   leftLine: OffsetLine;
 *   rightLine: OffsetLine;
 *   leftRay: OffsetLine;
 *   rightRay: OffsetLine;
 *   halfThicknessPx: number;
 * }} ConnectedWallEnd
 */

/**
 * @typedef {{
 *   anchor: Point;
 *   dir: Vec2;
 * }} OffsetLine
 */

/**
 * @typedef {{
 *   left: Point;
 *   right: Point;
 *   degree: number;
 *   nodeKey: string;
 *   warnings: string[];
 * }} JunctionEndVertices
 */

/**
 * @typedef {{
 *   end: ConnectedWallEnd;
 *   face: 'left' | 'right';
 *   point: Point;
 *   ray: OffsetLine;
 * }} WallEndFace
 */

/**
 * @typedef {{
 *   from: WallEndFace;
 *   to: WallEndFace;
 * }} FacePairing
 */

/**
 * @typedef {{
 *   left: Point;
 *   right: Point;
 *   outer: Point;
 *   inner: Point;
 * }} WeldedEndSides
 */

/**
 * @typedef {{
 *   nodeKey: string;
 *   degree: number;
 *   connectedWallIds: string[];
 *   sortedWallAngles: Array<{ wallId: string; end: 'start' | 'end'; angle: number }>;
 *   wallEndsInAngleOrder: Array<{
 *     wallId: string;
 *     end: 'start' | 'end';
 *     angle: number;
 *     leftPoint: Point;
 *     rightPoint: Point;
 *   }>;
 *   facePairings: Array<{
 *     fromWallId: string;
 *     fromFace: 'left' | 'right';
 *     toWallId: string;
 *     toFace: 'left' | 'right';
 *     intersection: Point | null;
 *     fallbackUsed: boolean;
 *     fallbackReason: string | null;
 *   }>;
 *   finalVerticesByWall: Record<string, {
 *     startLeft?: Point;
 *     startRight?: Point;
 *     endLeft?: Point;
 *     endRight?: Point;
 *   }>;
 *   closurePolygons: Array<{
 *     ownerWallId: string;
 *     polygon: Point[];
 *     winding: 'clockwise' | 'counterclockwise' | 'degenerate';
 *     leftOpen: boolean;
 *   }>;
 *   warnings: string[];
 *   calculatedIntersections: Array<{
 *     wallId: string;
 *     side: 'left' | 'right';
 *     adjacentWallId: string | null;
 *     usedAdjacentSide: 'left' | 'right' | null;
 *     intersection: Point | null;
 *     distanceFromNode: number | null;
 *     fallbackUsed: boolean;
 *     fallbackReason: string | null;
 *   }>;
 * }} JunctionDebug
 */

/**
 * @typedef {{
 *   nodeKey: string;
 *   ownerWallId: string;
 *   polygon: Point[];
 * }} JunctionClosurePolygon
 */

/**
 * @typedef {{
 *   wallId: string;
 *   bodyPolygon: Point[] | null;
 *   startCaps: Point[];
 *   endCaps: Point[];
 *   debug: {
 *     startNodeKey: string;
 *     endNodeKey: string;
 *     startDegree: number;
 *     endDegree: number;
 *     startLeft: Point;
 *     startRight: Point;
 *     endLeft: Point;
 *     endRight: Point;
 *     start: WeldedEndSides;
 *     end: WeldedEndSides;
 *     junctions: { start: JunctionDebug | null; end: JunctionDebug | null };
 *     warnings: string[];
 *   };
 * }} JunctionAwareWallEntry
 */

/**
 * @typedef {{
 *   walls: Map<string, JunctionAwareWallEntry>;
 *   pivotWelds: Map<string, WeldedEndSides>;
 *   junctions: Map<string, JunctionDebug>;
 *   closurePolygons: JunctionClosurePolygon[];
 * }} JunctionAwareWallGeometry
 */

/**
 * @param {Vec2} v
 * @returns {Vec2}
 */
export function normalize(v) {
  const len = Math.hypot(v.x, v.y);
  if (len < EPS) return { x: 0, y: 0 };
  return { x: v.x / len, y: v.y / len };
}

/**
 * @param {Vec2} a
 * @param {Vec2} b
 * @returns {number}
 */
export function dot(a, b) {
  return a.x * b.x + a.y * b.y;
}

/**
 * @param {Vec2} a
 * @param {Vec2} b
 * @returns {number}
 */
export function cross(a, b) {
  return a.x * b.y - a.y * b.x;
}

/**
 * @param {Vec2} v
 * @returns {number}
 */
export function angleOf(v) {
  const angle = Math.atan2(v.y, v.x);
  return angle < 0 ? angle + Math.PI * 2 : angle;
}

/**
 * @param {Point} a
 * @param {Point} b
 * @returns {number}
 */
export function distance(a, b) {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

/**
 * @param {Point} a
 * @param {Point} b
 * @returns {Point}
 */
function midpoint(a, b) {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

/**
 * @param {Point} a
 * @param {Point} b
 * @returns {Point}
 */
export function add(a, b) {
  return [a[0] + b[0], a[1] + b[1]];
}

/**
 * @param {Point} a
 * @param {Point} b
 * @returns {Point}
 */
export function sub(a, b) {
  return [a[0] - b[0], a[1] - b[1]];
}

/**
 * @param {Vec2} v
 * @param {number} s
 * @returns {Point}
 */
export function mul(v, s) {
  return [v.x * s, v.y * s];
}

/**
 * @param {Point} a
 * @param {Point} b
 * @param {number} [eps]
 * @returns {boolean}
 */
export function almostEqualPoint(a, b, eps = NODE_EPS_PX) {
  return distance(a, b) <= eps;
}

/**
 * @param {Point[]} polygon
 * @returns {number}
 */
function signedArea(polygon) {
  let area = 0;
  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    area += a[0] * b[1] - b[0] * a[1];
  }
  return area / 2;
}

/**
 * @param {Point[]} polygon
 * @returns {'clockwise' | 'counterclockwise' | 'degenerate'}
 */
function polygonWinding(polygon) {
  const area = signedArea(polygon);
  if (Math.abs(area) < EPS) return 'degenerate';
  return area > 0 ? 'clockwise' : 'counterclockwise';
}

/**
 * @param {{ x: number; y: number } | Point} point
 * @returns {string}
 */
export function makeNodeKey(point) {
  const x = Array.isArray(point) ? point[0] : point.x;
  const y = Array.isArray(point) ? point[1] : point.y;
  return `${Math.round(x / NODE_EPS_PX)},${Math.round(y / NODE_EPS_PX)}`;
}

/**
 * @param {Point} p1
 * @param {Vec2} d1
 * @param {Point} p2
 * @param {Vec2} d2
 * @returns {Point | null}
 */
export function lineLineIntersection(p1, d1, p2, d2) {
  const denom = cross(d1, d2);
  if (Math.abs(denom) < PARALLEL_EPS) return null;
  const delta = { x: p2[0] - p1[0], y: p2[1] - p1[1] };
  const t = cross(delta, d2) / denom;
  return [p1[0] + d1.x * t, p1[1] + d1.y * t];
}

/**
 * @param {Point} p
 * @returns {boolean}
 */
function isFinitePoint(p) {
  return Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]);
}

/**
 * @param {Point} node
 * @param {Vec2} normal
 * @param {number} halfThicknessPx
 * @returns {Point}
 */
function offsetPoint(node, normal, halfThicknessPx) {
  return add(node, mul(normal, halfThicknessPx));
}

/**
 * @param {DesignerGraph} graph
 * @param {DesignerWall} wall
 * @param {number} pixelsPerMeter
 * @returns {number}
 */
function halfThicknessForWall(graph, wall, pixelsPerMeter) {
  const thicknessM = wall.thicknessMeters ?? graph.wallSettings.thicknessMeters ?? DEFAULT_WALL_THICKNESS_M;
  return (thicknessM * pixelsPerMeter) / 2;
}

/**
 * @param {ConnectedWallEnd} end
 * @returns {boolean}
 */
function isValidWallEnd(end) {
  return Math.hypot(end.directionFromNode.x, end.directionFromNode.y) > EPS;
}

/**
 * @param {DesignerGraph} graph
 * @param {number} pixelsPerMeter
 * @returns {{
 *   byNode: Map<string, ConnectedWallEnd[]>;
 *   byWallEnd: Map<string, ConnectedWallEnd>;
 *   nodePoints: Map<string, Point>;
 * }}
 */
function collectWallEnds(graph, pixelsPerMeter) {
  /** @type {Map<string, ConnectedWallEnd[]>} */
  const byNode = new Map();
  /** @type {Map<string, ConnectedWallEnd>} */
  const byWallEnd = new Map();
  /** @type {Map<string, Point>} */
  const nodePoints = new Map();

  for (const wall of graph.walls) {
    const startPt = getPointById(/** @type {import('./floorPlanDesignerState.js').DesignerState} */ (graph), wall.startPointId);
    const endPt = getPointById(/** @type {import('./floorPlanDesignerState.js').DesignerState} */ (graph), wall.endPointId);
    if (!startPt || !endPt) continue;
    if (Math.hypot(endPt.x - startPt.x, endPt.y - startPt.y) < MIN_WALL_LENGTH_METERS * pixelsPerMeter) continue;

    const starts = [
      { end: 'start', node: /** @type {Point} */ ([startPt.x, startPt.y]), other: /** @type {Point} */ ([endPt.x, endPt.y]) },
      { end: 'end', node: /** @type {Point} */ ([endPt.x, endPt.y]), other: /** @type {Point} */ ([startPt.x, startPt.y]) },
    ];

    for (const item of starts) {
      const dir = normalize({ x: item.other[0] - item.node[0], y: item.other[1] - item.node[1] });
      const halfThicknessPx = halfThicknessForWall(graph, wall, pixelsPerMeter);
      const leftNormal = { x: -dir.y, y: dir.x };
      const rightNormal = { x: dir.y, y: -dir.x };
      const nodeKey = makeNodeKey(item.node);
      const leftPoint = offsetPoint(item.node, leftNormal, halfThicknessPx);
      const rightPoint = offsetPoint(item.node, rightNormal, halfThicknessPx);
      const connectedEnd = /** @type {ConnectedWallEnd} */ ({
        wallId: wall.id,
        end: item.end,
        nodeKey,
        nodePoint: item.node,
        otherPoint: item.other,
        directionFromNode: dir,
        angle: angleOf(dir),
        leftNormal,
        rightNormal,
        leftPoint,
        rightPoint,
        halfThicknessPx,
        leftLine: {
          anchor: leftPoint,
          dir,
        },
        rightLine: {
          anchor: rightPoint,
          dir,
        },
        leftRay: {
          anchor: leftPoint,
          dir,
        },
        rightRay: {
          anchor: rightPoint,
          dir,
        },
      });
      if (!isValidWallEnd(connectedEnd)) continue;
      if (!byNode.has(nodeKey)) byNode.set(nodeKey, []);
      byNode.get(nodeKey).push(connectedEnd);
      if (!nodePoints.has(nodeKey)) nodePoints.set(nodeKey, item.node);
      byWallEnd.set(`${wall.id}|${item.end}`, connectedEnd);
    }
  }

  for (const ends of byNode.values()) {
    ends.sort((a, b) => {
      if (a.angle !== b.angle) return a.angle - b.angle;
      if (a.wallId !== b.wallId) return a.wallId.localeCompare(b.wallId);
      return a.end.localeCompare(b.end);
    });
  }

  return { byNode, byWallEnd, nodePoints };
}

/**
 * @param {ConnectedWallEnd[]} ends
 * @returns {ConnectedWallEnd[]}
 */
function sortWallEndsAroundNode(ends) {
  return [...ends].sort((a, b) => {
    if (a.angle !== b.angle) return a.angle - b.angle;
    if (a.wallId !== b.wallId) return a.wallId.localeCompare(b.wallId);
    return a.end.localeCompare(b.end);
  });
}

/**
 * @param {ConnectedWallEnd} end
 * @returns {{ left: WallEndFace; right: WallEndFace }}
 */
function buildWallEndFaces(end) {
  return {
    left: {
      end,
      face: 'left',
      point: end.leftPoint,
      ray: end.leftRay,
    },
    right: {
      end,
      face: 'right',
      point: end.rightPoint,
      ray: end.rightRay,
    },
  };
}

/**
 * @param {ConnectedWallEnd[]} sorted
 * @returns {FacePairing[]}
 */
function pairNeighborFacesAroundJunction(sorted) {
  if (sorted.length <= 1) return [];
  /** @type {FacePairing[]} */
  const pairings = [];
  for (let i = 0; i < sorted.length; i += 1) {
    const current = sorted[i];
    const next = sorted[(i + 1) % sorted.length];
    const currentFaces = buildWallEndFaces(current);
    const nextFaces = buildWallEndFaces(next);
    pairings.push({
      from: currentFaces.left,
      to: nextFaces.right,
    });
  }
  return pairings;
}

/**
 * @param {FacePairing} pairing
 * @param {Point} nodePoint
 * @param {number} maxJoinDistance
 * @returns {{ point: Point; intersection: Point | null; fallbackUsed: boolean; fallbackReason: string | null; distanceFromNode: number | null }}
 */
function intersectFaceEdges(pairing, nodePoint, maxJoinDistance) {
  const join = lineLineIntersection(
    pairing.from.ray.anchor,
    pairing.from.ray.dir,
    pairing.to.ray.anchor,
    pairing.to.ray.dir,
  );

  if (!join || !isFinitePoint(join)) {
    return {
      point: midpoint(pairing.from.point, pairing.to.point),
      intersection: null,
      fallbackUsed: true,
      fallbackReason: 'parallel_face_edges',
      distanceFromNode: null,
    };
  }

  const dist = distance(nodePoint, join);
  if (dist > maxJoinDistance) {
    return {
      point: midpoint(pairing.from.point, pairing.to.point),
      intersection: join,
      fallbackUsed: true,
      fallbackReason: 'intersection_too_far',
      distanceFromNode: dist,
    };
  }

  return {
    point: join,
    intersection: join,
    fallbackUsed: false,
    fallbackReason: null,
    distanceFromNode: dist,
  };
}

/**
 * @param {Map<string, JunctionEndVertices>} endVertices
 * @param {ConnectedWallEnd} end
 * @param {'left' | 'right'} face
 * @param {Point} point
 */
function setFaceVertex(endVertices, end, face, point) {
  const key = `${end.wallId}|${end.end}`;
  const current = endVertices.get(key) ?? {
    left: end.leftPoint,
    right: end.rightPoint,
    degree: 1,
    nodeKey: end.nodeKey,
    warnings: [],
  };
  current[face] = point;
  endVertices.set(key, current);
}

/**
 * @param {Map<string, JunctionEndVertices>} endVertices
 * @param {Map<string, string[]>} warningsByWallEnd
 * @param {ConnectedWallEnd} end
 * @param {string} warning
 */
function addEndWarning(endVertices, warningsByWallEnd, end, warning) {
  const key = `${end.wallId}|${end.end}`;
  if (!warningsByWallEnd.has(key)) warningsByWallEnd.set(key, []);
  warningsByWallEnd.get(key).push(warning);
  const current = endVertices.get(key);
  if (current) current.warnings.push(warning);
}

/**
 * @param {ConnectedWallEnd[]} sorted
 * @param {JunctionDebug | null} junctionDebug
 * @param {number} pixelsPerMeter
 * @returns {{
 *   endVertices: Map<string, JunctionEndVertices>;
 *   warningsByWallEnd: Map<string, string[]>;
 *   closurePolygon: JunctionClosurePolygon | null;
 * }}
 */
function buildJunctionVertices(sorted, junctionDebug, pixelsPerMeter) {
  /** @type {Map<string, JunctionEndVertices>} */
  const endVertices = new Map();
  /** @type {Map<string, string[]>} */
  const warningsByWallEnd = new Map();
  const degree = sorted.length;

  for (const end of sorted) {
    endVertices.set(`${end.wallId}|${end.end}`, {
      left: end.leftPoint,
      right: end.rightPoint,
      degree,
      nodeKey: end.nodeKey,
      warnings: [],
    });
  }

  if (degree <= 1) {
    return { endVertices, warningsByWallEnd, closurePolygon: null };
  }

  const wallThicknessPx = Math.max(...sorted.map((end) => end.halfThicknessPx * 2));
  const maxJoinDistance = Math.max(wallThicknessPx * MAX_JOIN_DISTANCE_FACTOR, 0.35 * pixelsPerMeter);
  const pairings = pairNeighborFacesAroundJunction(sorted);
  /** @type {Point[]} */
  const closureVertices = [];

  for (const pairing of pairings) {
    const result = intersectFaceEdges(pairing, pairing.from.end.nodePoint, maxJoinDistance);
    closureVertices.push(result.point);
    setFaceVertex(endVertices, pairing.from.end, pairing.from.face, result.point);
    setFaceVertex(endVertices, pairing.to.end, pairing.to.face, result.point);

    if (result.fallbackUsed) {
      addEndWarning(
        endVertices,
        warningsByWallEnd,
        pairing.from.end,
        `${pairing.from.face}:${result.fallbackReason}`,
      );
      addEndWarning(
        endVertices,
        warningsByWallEnd,
        pairing.to.end,
        `${pairing.to.face}:${result.fallbackReason}`,
      );
    }

    junctionDebug?.facePairings.push({
      fromWallId: pairing.from.end.wallId,
      fromFace: pairing.from.face,
      toWallId: pairing.to.end.wallId,
      toFace: pairing.to.face,
      intersection: result.intersection,
      fallbackUsed: result.fallbackUsed,
      fallbackReason: result.fallbackReason,
    });

    for (const face of [pairing.from, pairing.to]) {
      junctionDebug?.calculatedIntersections.push({
        wallId: face.end.wallId,
        side: face.face,
        adjacentWallId: face === pairing.from ? pairing.to.end.wallId : pairing.from.end.wallId,
        usedAdjacentSide: face === pairing.from ? pairing.to.face : pairing.from.face,
        intersection: result.intersection,
        distanceFromNode: result.distanceFromNode,
        fallbackUsed: result.fallbackUsed,
        fallbackReason: result.fallbackReason,
      });
    }
  }

  let closurePolygon = /** @type {JunctionClosurePolygon | null} */ (null);
  const dedupedClosure = dedupePolygonPoints(closureVertices);
  const closureCandidate =
    degree === 2
      ? dedupePolygonPoints([sorted[0].nodePoint, ...dedupedClosure])
      : dedupedClosure;
  const winding = polygonWinding(closureCandidate);
  if (closureCandidate.length >= 3 && winding !== 'degenerate') {
    const ownerWallId = [...new Set(sorted.map((end) => end.wallId))].sort()[0];
    closurePolygon = {
      nodeKey: sorted[0].nodeKey,
      ownerWallId,
      polygon: closureCandidate,
    };
    junctionDebug?.closurePolygons.push({
      ownerWallId,
      polygon: closureCandidate,
      winding,
      leftOpen: false,
    });
  } else {
    junctionDebug?.warnings.push('closure_polygon_degenerate');
  }

  return { endVertices, warningsByWallEnd, closurePolygon };
}

/**
 * @param {Map<string, ConnectedWallEnd[]>} byNode
 * @returns {Map<string, JunctionDebug>}
 */
function buildJunctionDebug(byNode) {
  /** @type {Map<string, JunctionDebug>} */
  const debug = new Map();
  for (const [nodeKey, sorted] of byNode.entries()) {
    debug.set(nodeKey, {
      nodeKey,
      degree: sorted.length,
      connectedWallIds: sorted.map((end) => end.wallId),
      sortedWallAngles: sorted.map((end) => ({
        wallId: end.wallId,
        end: end.end,
        angle: end.angle,
      })),
      wallEndsInAngleOrder: sorted.map((end) => ({
        wallId: end.wallId,
        end: end.end,
        angle: end.angle,
        leftPoint: end.leftPoint,
        rightPoint: end.rightPoint,
      })),
      facePairings: [],
      finalVerticesByWall: {},
      closurePolygons: [],
      warnings: [],
      calculatedIntersections: [],
    });
  }
  return debug;
}

/**
 * @param {JunctionDebug | null} junctionDebug
 * @param {DesignerWall} wall
 * @param {Point} startLeft
 * @param {Point} startRight
 * @param {Point} endLeft
 * @param {Point} endRight
 */
function recordFinalVertices(junctionDebug, wall, startLeft, startRight, endLeft, endRight) {
  if (!junctionDebug) return;
  junctionDebug.finalVerticesByWall[wall.id] = {
    startLeft,
    startRight,
    endLeft,
    endRight,
  };
}

/**
 * @param {Point[]} polygon
 * @returns {Point[]}
 */
function dedupePolygonPoints(polygon) {
  /** @type {Point[]} */
  const deduped = [];
  for (const point of polygon) {
    const last = deduped[deduped.length - 1];
    if (!last || !almostEqualPoint(last, point, 1e-6)) deduped.push(point);
  }
  if (deduped.length > 1 && almostEqualPoint(deduped[0], deduped[deduped.length - 1], 1e-6)) {
    deduped.pop();
  }
  return deduped;
}

/**
 * @param {Point} startLeft
 * @param {Point} startRight
 * @param {Point} endLeft
 * @param {Point} endRight
 * @returns {Point[]}
 */
function buildWallPolygonFromJunctionVertices(startLeft, startRight, endLeft, endRight) {
  return dedupePolygonPoints([
    startLeft,
    endLeft,
    endRight,
    startRight,
  ]);
}

/**
 * @param {string} pivotId
 * @param {string} wallId
 * @returns {string}
 */
export function junctionWeldedEndKey(pivotId, wallId) {
  return `${pivotId}|${wallId}`;
}

/**
 * Compatibility adapter for older callers/tests. Values are keyed by point id, but
 * the coordinates are produced by the snapped junction-aware solver.
 * @param {DesignerGraph} graph
 * @param {number} pixelsPerMeter
 * @returns {Map<string, WeldedEndSides>}
 */
export function buildJunctionVerticesAtAllPivots(graph, pixelsPerMeter) {
  const geometry = buildJunctionAwareWallGeometry({
    points: graph.points,
    walls: graph.walls,
    pixelsPerMeter,
    wallSettings: graph.wallSettings,
  });
  return geometry.pivotWelds;
}

/**
 * @param {{
 *   points: import('./floorPlanDesignerState.js').DesignerPoint[];
 *   walls: DesignerWall[];
 *   pixelsPerMeter: number;
 *   wallThicknessMeters?: number;
 *   wallSettings?: { thicknessMeters?: number; thicknessDirection?: 'inside' | 'outside' };
 * }} input
 * @returns {JunctionAwareWallGeometry}
 */
export function buildJunctionAwareWallGeometry(input) {
  const graph = toDesignerGraph({
    points: input.points,
    walls: input.walls.map((wall) => ({
      ...wall,
      thicknessMeters: wall.thicknessMeters ?? input.wallThicknessMeters,
    })),
    wallSettings: input.wallSettings ?? {
      thicknessMeters: input.wallThicknessMeters ?? DEFAULT_WALL_THICKNESS_M,
      thicknessDirection: 'outside',
    },
  });
  const pixelsPerMeter = input.pixelsPerMeter > 0 ? input.pixelsPerMeter : 100;
  const { byNode, byWallEnd } = collectWallEnds(graph, pixelsPerMeter);
  const junctions = buildJunctionDebug(byNode);
  /** @type {Map<string, JunctionEndVertices>} */
  const resolvedEndVertices = new Map();
  /** @type {JunctionClosurePolygon[]} */
  const closurePolygons = [];

  for (const [nodeKey, ends] of byNode.entries()) {
    const sorted = sortWallEndsAroundNode(ends);
    byNode.set(nodeKey, sorted);
    const result = buildJunctionVertices(
      sorted,
      junctions.get(nodeKey) ?? null,
      pixelsPerMeter,
    );
    for (const [key, vertices] of result.endVertices.entries()) {
      resolvedEndVertices.set(key, vertices);
    }
    if (result.closurePolygon) {
      closurePolygons.push(result.closurePolygon);
    }
  }

  /** @type {Map<string, JunctionAwareWallEntry>} */
  const walls = new Map();
  /** @type {Map<string, WeldedEndSides>} */
  const pivotWelds = new Map();

  for (const wall of graph.walls) {
    const startEnd = byWallEnd.get(`${wall.id}|start`);
    const endEnd = byWallEnd.get(`${wall.id}|end`);
    if (!startEnd || !endEnd) continue;

    const startSides = resolvedEndVertices.get(`${wall.id}|start`) ?? {
      left: startEnd.leftPoint,
      right: startEnd.rightPoint,
      degree: 1,
      nodeKey: startEnd.nodeKey,
      warnings: [],
    };
    const endSides = resolvedEndVertices.get(`${wall.id}|end`) ?? {
      left: endEnd.leftPoint,
      right: endEnd.rightPoint,
      degree: 1,
      nodeKey: endEnd.nodeKey,
      warnings: [],
    };
    const startLeft = startSides.left;
    const startRight = startSides.right;
    const endLeft = endSides.right;
    const endRight = endSides.left;
    const bodyPolygon = buildWallPolygonFromJunctionVertices(
      startLeft,
      startRight,
      endLeft,
      endRight,
    );
    const validatedBody = bodyPolygon.length === 4
      ? validatePivotWallPolygon(bodyPolygon, wall.id)
      : null;
    const warnings = [...startSides.warnings, ...endSides.warnings];
    recordFinalVertices(
      junctions.get(startSides.nodeKey) ?? null,
      wall,
      startLeft,
      startRight,
      endLeft,
      endRight,
    );
    recordFinalVertices(
      junctions.get(endSides.nodeKey) ?? null,
      wall,
      startLeft,
      startRight,
      endLeft,
      endRight,
    );

    const startPointId = wall.startPointId || startEnd.nodeKey;
    const endPointId = wall.endPointId || endEnd.nodeKey;
    pivotWelds.set(junctionWeldedEndKey(startPointId, wall.id), {
      left: startLeft,
      right: startRight,
      outer: startLeft,
      inner: startRight,
    });
    pivotWelds.set(junctionWeldedEndKey(endPointId, wall.id), {
      left: endLeft,
      right: endRight,
      outer: endLeft,
      inner: endRight,
    });

    walls.set(wall.id, {
      wallId: wall.id,
      bodyPolygon: validatedBody,
      startCaps: [],
      endCaps: [],
      debug: {
        startNodeKey: startSides.nodeKey,
        endNodeKey: endSides.nodeKey,
        startDegree: startSides.degree,
        endDegree: endSides.degree,
        startLeft,
        startRight,
        endLeft,
        endRight,
        start: {
          outer: startLeft,
          inner: startRight,
          left: startLeft,
          right: startRight,
        },
        end: {
          outer: endLeft,
          inner: endRight,
          left: endLeft,
          right: endRight,
        },
        junctions: {
          start: junctions.get(startSides.nodeKey) ?? null,
          end: junctions.get(endSides.nodeKey) ?? null,
        },
        warnings,
      },
    });
  }

  return { walls, pivotWelds, junctions, closurePolygons };
}
